import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { authAPI, rbacAPI } from '@/lib/api';

const AuthContext = createContext(null);

const DEFAULT_ROLES = ['admin', 'manager', 'agent'];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const saved = localStorage.getItem('user');
      const token = localStorage.getItem('token');
      if (saved && token) return JSON.parse(saved);
    } catch {}
    return null;
  });
  const [loading, setLoading] = useState(() => {
    const saved = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    return !(saved && token);
  });
  const [userPermissions, setUserPermissions] = useState(null);
  const [isAdminPermissions, setIsAdminPermissions] = useState(false);

  const fetchPermissions = useCallback(async () => {
    try {
      const res = await authAPI.myPermissions();
      setUserPermissions(res.data.permissions || {});
      setIsAdminPermissions(!!res.data.is_admin);
    } catch {
      setUserPermissions(null);
      setIsAdminPermissions(false);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (token && savedUser) {
      try {
        const parsed = JSON.parse(savedUser);
        setUser(parsed);
      } catch {}
      try {
        const [meRes] = await Promise.all([authAPI.me(), fetchPermissions()]);
        setUser(meRes.data);
        localStorage.setItem('user', JSON.stringify(meRes.data));
      } catch (err) {
        if (err?.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('userPermissions');
          setUser(null);
          setUserPermissions(null);
        }
      }
    }
    setLoading(false);
  }, [fetchPermissions]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback((token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    // Fetch permissions after login
    setTimeout(() => fetchPermissions(), 100);
  }, [fetchPermissions]);

  const logout = useCallback(async () => {
    try { await authAPI.logout(); } catch {}
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userPermissions');
    setUser(null);
    setUserPermissions(null);
    setIsAdminPermissions(false);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth, userPermissions, isAdminPermissions }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
